import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { VerifiedUser, GpsFixed, Star, PhotoLibrary, Description, HelpOutline, AccountBalanceWallet } from '@mui/icons-material';
import { brand, text, status as statusTokens } from '../../theme/colors';

/** The one rule behind every fact here. Same wording as the backend. */
export const REPUTATION_RULE = 'Only jobs paid through the Eqaya project wallet count. Cash jobs build nothing here.';

/**
 * "Why Eqaya trusts this contractor" — one line of evidence, built only
 * from things Eqaya actually did or recorded (verifier site visits,
 * approved documents, public reviews, portfolio). When there's little
 * evidence it says so plainly rather than hiding the line: a client
 * choosing between strangers is better served by "not yet field-verified"
 * than by silence.
 *
 * `facts` is the object from ContractorTrustFactsService (contractor list
 * `trust`, profile `trust_facts`, quotation `contractor_trust`).
 */
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export function trustFactItems(facts) {
  if (!facts) return [];
  const items = [];
  if (facts.fieldVerified) {
    items.push({
      key: 'verified',
      icon: <VerifiedUser />,
      strong: true,
      label: facts.siteVisits > 0
        ? `Field-verified by an Eqaya verifier — ${plural(facts.siteVisits, 'site visit')} with GPS proof`
        : 'Field-verified by an Eqaya verifier',
      tip: 'An independent Eqaya verifier checked this contractor in person: identity, business authority and past work sites, with geofenced photo proof.',
    });
  } else if (facts.documentsVerified > 0) {
    items.push({
      key: 'docs',
      icon: <Description />,
      label: 'Documents checked by Eqaya — field verification not yet done',
      tip: `${plural(facts.documentsVerified, 'document')} reviewed and approved by Eqaya. A verifier has not yet visited their past work in person.`,
    });
  } else {
    items.push({
      key: 'unverified',
      icon: <HelpOutline />,
      muted: true,
      label: 'Not yet verified by Eqaya',
      tip: 'Ask for an assessment visit or an independent verifier before releasing any payment.',
    });
  }
  if (facts.paidJobs > 0) {
    items.push({
      key: 'paid',
      icon: <AccountBalanceWallet />,
      label: `${plural(facts.paidJobs, 'job')} paid through Eqaya`,
      tip: REPUTATION_RULE,
    });
  } else {
    items.push({ key: 'nopaid', icon: <AccountBalanceWallet />, muted: true, label: 'No Eqaya-paid jobs yet', tip: REPUTATION_RULE });
  }
  if (facts.reviewCount > 0) {
    items.push({
      key: 'reviews',
      icon: <Star />,
      label: `${Number(facts.reviewAvg).toFixed(1)} from ${plural(facts.reviewCount, 'review')}`,
      tip: 'Reviews can only be left by a client after a stage has been paid through the Eqaya wallet.',
    });
  } else {
    items.push({ key: 'noreviews', icon: <Star />, muted: true, label: 'No reviews yet', tip: 'Reviews can only be left by a client after a stage has been paid through the Eqaya wallet.' });
  }
  if (facts.portfolioCount > 0) {
    items.push({ key: 'portfolio', icon: <PhotoLibrary />, label: plural(facts.portfolioCount, 'portfolio project') });
  }
  return items;
}

const TrustLine = ({ facts, compact = false, sx }) => {
  const items = trustFactItems(facts);
  if (items.length === 0) return null;
  return (
    <Box sx={{ mt: compact ? 0.5 : 1, ...sx }}>
      {!compact && (
        <Typography variant="caption" sx={{ color: text.tertiary, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', display: 'block', mb: 0.25 }}>
          Why Eqaya trusts this contractor
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1.25, rowGap: 0.25 }}>
        {items.map((it, i) => {
          const color = it.strong ? statusTokens.successDark : it.muted ? text.tertiary : text.secondary;
          const node = (
            <Box key={it.key} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color, fontSize: compact ? 12 : 13, fontWeight: it.strong ? 700 : 500, lineHeight: 1.4 }}>
              {React.cloneElement(it.icon, { sx: { fontSize: compact ? 14 : 16, color: it.strong ? statusTokens.success : it.muted ? text.tertiary : brand.primary } })}
              <span>{it.label}</span>
              {i < items.length - 1 && <Box component="span" sx={{ color: text.tertiary, ml: 0.75 }}>·</Box>}
            </Box>
          );
          return it.tip ? <Tooltip key={it.key} title={it.tip} arrow>{node}</Tooltip> : node;
        })}
      </Box>
      {facts?.fieldVerified && facts?.siteVisits > 0 && !compact && (
        <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 0.25 }}>
          <GpsFixed sx={{ fontSize: 12, verticalAlign: 'text-bottom', mr: 0.25 }} />
          Site visits are geofenced: the verifier's phone confirmed they stood at the address.
        </Typography>
      )}
      {!compact && (
        <Typography variant="caption" sx={{ color: text.tertiary, display: 'block', mt: 0.25 }}>
          Job counts and reviews come only from work paid through the Eqaya project wallet. Cash jobs don't appear here.
        </Typography>
      )}
    </Box>
  );
};

export default TrustLine;
